import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import {
  FaPlus,
  FaArrowLeft,
} from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import Assessments from "../Components/Assessments";
import LearnerAssessment from "../Components/LearnerAssessment";
import "../Styles/AssessmentsPage.css";

function AssessmentsPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { moduleId, moduleName } = state || {};

  const [showModal, setShowModal] = useState(false);
  const [assessmentVersion, setAssessmentVersion] = useState(0);

  const [formData, setFormData] = useState({
    assessment_name: "",
    information: "",
    type: "",
  });

  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    const loggedInUser = JSON.parse(localStorage.getItem("user"));
    setUserRole(loggedInUser?.user_role || "learner");
  }, []);

  const handleOpenModal = () => setShowModal(true);

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      assessment_name: "",
      information: "",
      type: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.assessment_name || !formData.information || !formData.type) {
      toast.error("Please fill all required fields.", {
        position: "top-right",
        autoClose: 1500,
      });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/insertAssessment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...formData,
            module_id: moduleId,
          }),
        },
      );

      const data = await response.json();

      if (data.success) {
        toast.success("Assessment saved successfully!", {
          position: "top-right",
          autoClose: 1000,
        });

        setFormData({
          assessment_name: "",
          information: "",
          type: "",
        });
        handleCloseModal();
        setAssessmentVersion((version) => version + 1);
      } else {
        toast.error(data.message || "Failed to save assessment.", {
          position: "top-right",
          autoClose: 1500,
        });
      }
    } catch (error) {
      console.error("Error saving assessment:", error);

      toast.error("An error occurred. Please try again.", {
        position: "top-right",
        autoClose: 1500,
      });
    } finally {
      setLoading(false);
    }
  };

  const isCreateFormValid =
    formData.assessment_name.trim() !== "" &&
    formData.information.trim() !== "" &&
    formData.type !== "";

  return (
    <div
      className="content-wrapper container assessments-page"
    >
      <header className="assessments-page__header">
        <div className="assessments-page__heading-group">
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              onClick={() =>
                navigate("/dashboard", { state: { activeTab: "modules" } })
              }
              className="assessments-page__back-button"
              aria-label="Back to modules"
              title="Back to modules"
            >
              <FaArrowLeft size={17} aria-hidden="true" />
            </button>

            <div className="assessments-page__title-copy">
              <span className="assessments-page__eyebrow">Module workspace</span>
              <h1>
                Assessments
              </h1>

              <p>
                {userRole === "admin" ? "Manage " : "Take "}
                assessments, questions, and publishing for this module.
              </p>
            </div>
          </div>

          <div className="assessments-page__module-pill">
            <span>Current module</span>
            <strong>{moduleName || "Module"}</strong>
          </div>
        </div>

        {userRole === "admin" && (
          <Button onClick={handleOpenModal} className="assessments-page__add-button">
            <FaPlus size={14} aria-hidden="true" />
            <span>Add assessment</span>
          </Button>
        )}
      </header>

      <main className="assessments-page__surface">
        {userRole === "admin" ? (
          <Assessments refreshVersion={assessmentVersion} />
        ) : (
          <LearnerAssessment />
        )}
      </main>

      {userRole === "admin" && (
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          centered
          className="assessment-edit-modal assessment-create-modal"
          dialogClassName="assessment-create-dialog"
          style={{ fontFamily: "Manrope-Regular" }}
        >
          <form onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>
                <span className="assessment-edit-modal__title">
                  Create assessment
                </span>
                <span className="assessment-edit-modal__subtitle">
                  Set up the core details now. Questions and settings can be
                  added after creation.
                </span>
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Form.Group className="assessment-edit-modal__field">
                <Form.Label>
                  Assessment name <span aria-hidden="true">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="assessment_name"
                  placeholder="Enter assessment name"
                  value={formData.assessment_name}
                  onChange={handleChange}
                  maxLength={120}
                  required
                />
                <Form.Text>
                  Use a short, recognizable title for learners.
                </Form.Text>
              </Form.Group>

              <Form.Group className="assessment-edit-modal__field">
                <div className="assessment-edit-modal__label-row">
                  <Form.Label>
                    Information <span aria-hidden="true">*</span>
                  </Form.Label>
                  <span>{formData.information.length}/500</span>
                </div>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="information"
                  placeholder="Describe what this assessment covers"
                  value={formData.information}
                  onChange={handleChange}
                  maxLength={500}
                  required
                />
                <Form.Text>
                  This description helps learners understand what to expect.
                </Form.Text>
              </Form.Group>

              <Form.Group>
                <Form.Label>
                  Assessment type <span aria-hidden="true">*</span>
                </Form.Label>
                <Form.Select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>
                    Select an assessment type
                  </option>
                  <option value="Practice">Practice</option>
                  <option value="Exam">Exam</option>
                </Form.Select>
                <Form.Text>
                  Practice supports repeat learning; Exam is for formal attempts.
                </Form.Text>
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <span className="assessment-edit-modal__required-note">
                
              </span>
              <div className="assessment-edit-modal__footer-actions">
                <Button
                  type="button"
                  variant="light"
                  onClick={handleCloseModal}
                  disabled={loading}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  className="assessment-edit-modal__save"
                  disabled={loading || !isCreateFormValid}
                >
                  {loading ? "Creating assessment..." : "Create assessment"}
                </Button>
              </div>
            </Modal.Footer>
          </form>
        </Modal>
      )}

      <ToastContainer />
    </div>
  );
}

export default AssessmentsPage;
